import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { getCurrentUser, logoutUser, setUserCsrfToken } from "@/lib/api";

const UserContext = createContext(null);

// A banned account is rejected by /auth/me with 403 ACCOUNT_BANNED. The
// context still just signs out, but the visitor is told why exactly once
// instead of silently bouncing back to a signed-out page.
let bannedNoticeShown = false;
const noticeBanned = (err) => {
  if (bannedNoticeShown) return;
  if (err?.response?.status !== 403) return;
  if (err?.response?.data?.detail?.code !== "ACCOUNT_BANNED") return;
  bannedNoticeShown = true;
  toast.error(err.response.data.detail.message || "This account is not available.");
};

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const data = await getCurrentUser();
      setUserCsrfToken(data.csrf_token);
      setUser(data.user);
      return data.user;
    } catch (err) {
      noticeBanned(err);
      throw err;
    }
  };

  useEffect(() => {
    let active = true;
    getCurrentUser()
      .then((data) => {
        if (!active) return;
        setUserCsrfToken(data.csrf_token);
        setUser(data.user);
      })
      .catch((err) => {
        if (active) setUser(null);
        noticeBanned(err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const signIn = (authenticatedUser) => setUser(authenticatedUser);

  const signOut = async () => {
    await logoutUser().catch(() => undefined);
    setUser(null);
  };

  return (
    <UserContext.Provider value={{ user, setUser, signIn, signOut, refresh, loading }}>
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => useContext(UserContext);
